/**
 * Profile Page
 * Profilseite mit API v1.2 Struktur
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Profile.module.css';
import { userApi, statsApi, getMediaUrl } from '../api';
import { getSocialPlatform } from '../utils/socialLinks';

function Profile({ username: propUsername }) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [newGenre, setNewGenre] = useState('');
  const [newSocialLink, setNewSocialLink] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [stats, setStats] = useState({
    manga_count: 0,
    band_count: 0,
    completed_manga_count: 0,
    wishlist_count: 0
  });
  const [formData, setFormData] = useState({
    username: propUsername || 'User',
    email: '',
    bio: '',
    favorite_genres: [],
    social_links: [],
  });
  const [originalData, setOriginalData] = useState(null);
  const fileInputRef = useRef(null);

  // Profil und Stats beim Laden abrufen
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Profil und Stats parallel laden
        const [profileResult, statsResult] = await Promise.all([
          userApi.getProfile(),
          statsApi.getOverview(),
        ]);

        if (profileResult.success && profileResult.data) {
          const user = profileResult.data;
          setFormData({
            username: user.username || propUsername || 'User',
            email: user.email || '',
            bio: user.bio || '',
            favorite_genres: user.favorite_genres || [],
            social_links: user.social_links || [],
          });
          setOriginalData(user);
          
          // Avatar URL aus media_key
          if (user.avatar_has && user.avatar_media_key) {
            setAvatarUrl(getMediaUrl(user.avatar_media_key));
          }
        }

        if (statsResult.success && statsResult.data) {
          setStats(statsResult.data);
        }

      } catch (err) {
        console.error('Fehler beim Laden des Profils:', err);
        setError(t('profile.loadError'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propUsername]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      // API v1.2: PATCH /users/me mit snake_case Feldern
      const result = await userApi.updateProfile({
        username: formData.username,
        bio: formData.bio,
        favorite_genres: formData.favorite_genres,
        social_links: formData.social_links
      });
      
      if (result.success) {
        setOriginalData(formData);
        setIsEditing(false);
        setMessage({ type: 'success', text: t('profile.saveSuccess') });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setError(result.error?.message || t('profile.saveError'));
      }
    } catch (err) {
      setError(err.message || t('profile.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalData) {
      setFormData({
        username: originalData.username || propUsername || 'User',
        email: originalData.email || '',
        bio: originalData.bio || '',
        favorite_genres: originalData.favorite_genres || [],
        social_links: originalData.social_links || [],
      });
    }
    setIsEditing(false);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError(t('profile.avatarInvalidType'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError(t('profile.avatarTooLarge'));
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        // Avatar-Upload ist in API v1.2 nicht spezifiziert
        // Hier wird nur die Preview angezeigt
        setAvatarUrl(event.target?.result);
        setMessage({ type: 'info', text: t('profile.avatarUploadTodo') });
        setTimeout(() => setMessage(null), 3000);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError(t('profile.avatarUploadError'));
    }
  };

  const handleAddGenre = () => {
    const trimmedGenre = newGenre.trim();
    if (trimmedGenre && !formData.favorite_genres.includes(trimmedGenre)) {
      setFormData(prev => ({
        ...prev,
        favorite_genres: [...prev.favorite_genres, trimmedGenre]
      }));
      setNewGenre('');
    }
  };

  const handleRemoveGenre = (genreToRemove) => {
    setFormData(prev => ({
      ...prev,
      favorite_genres: prev.favorite_genres.filter(genre => genre !== genreToRemove)
    }));
  };

  const handleGenreKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddGenre();
    }
  };

  const handleAddSocialLink = () => {
    const trimmedLink = newSocialLink.trim();
    if (trimmedLink && !formData.social_links.includes(trimmedLink)) {
      setFormData(prev => ({
        ...prev,
        social_links: [...prev.social_links, trimmedLink]
      }));
      setNewSocialLink('');
    }
  };

  const handleRemoveSocialLink = (linkToRemove) => {
    setFormData(prev => ({
      ...prev,
      social_links: prev.social_links.filter(link => link !== linkToRemove)
    }));
  };

  const handleSocialLinkKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSocialLink();
    }
  };

  if (isLoading) {
    return (
      <div className={styles.profileContainer}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>{t('profile.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profileContainer}>
      {/* Messages */}
      {message && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          <span>{message.type === 'success' ? '✅' : '❌'}</span>
          <p>{message.text}</p>
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {error && (
        <div className={`${styles.message} ${styles.error}`}>
          <span>❌</span>
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Profile Header */}
      <div className={styles.profileHeader}>
        <div className={styles.avatarSection}>
          {avatarUrl ? (
            <img 
              src={avatarUrl} 
              alt="Avatar" 
              className={styles.avatarLarge}
              onClick={handleAvatarClick}
              onError={() => setAvatarUrl(null)}
            />
          ) : (
            <div className={styles.avatarLarge} onClick={handleAvatarClick}>
              {formData.username.charAt(0).toUpperCase()}
            </div>
          )}
          <button className={styles.changeAvatarBtn} onClick={handleAvatarClick}>
            📷
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleAvatarChange}
            accept="image/*"
            style={{ display: 'none' }}
          />
        </div>
        <div className={styles.headerInfo}>
          <h1>{formData.username}</h1>
          {formData.bio && <p className={styles.userBio}>{formData.bio}</p>}
          {originalData?.id && (
            <div className={styles.userId}>
              <span className={styles.userIdLabel}>{t('profile.yourUserId')}</span>
              <code className={styles.userIdValue}>#{originalData.id}</code>
              <button
                className={styles.userIdCopy}
                type="button"
                title={t('profile.copyTitle')}
                onClick={() => {
                  navigator.clipboard.writeText(String(originalData.id));
                }}
              >
                📋
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Profile Content */}
      <div className={styles.profileContent}>
        <div className={styles.contentHeader}>
          <h2>{t('profile.profileInfo')}</h2>
          {!isEditing && (
            <button className={styles.editBtn} onClick={() => setIsEditing(true)}>
              ✏️ {t('profile.edit')}
            </button>
          )}
        </div>

        {isEditing ? (
          <form onSubmit={handleSubmit} className={styles.editForm}>
            <div className={styles.formGroup}>
              <label htmlFor="username">{t('profile.username')}</label>
              <input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleInputChange}
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email">{t('profile.email')}</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className={styles.input}
                disabled
              />
              <span className={styles.fieldHint}>{t('profile.emailCannotChange')}</span>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="bio">{t('profile.bio')}</label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                className={styles.textarea}
                rows={4}
                placeholder={t('profile.bioPlaceholder')}
              />
            </div>

            <div className={styles.formGroup}>
              <label>{t('profile.socialLinks')}</label>
              <div className={styles.genreEditor}>
                <div className={styles.genreTagsEdit}>
                  {formData.social_links.map((link, index) => {
                    const platform = getSocialPlatform(link);
                    const Icon = platform?.Icon;
                    return (
                      <span key={index} className={styles.genreTagEdit} title={link}>
                        {Icon && (
                          <Icon className={styles.socialLinkIcon} style={platform.color ? { color: platform.color } : undefined} />
                        )}
                        {platform?.label || link}
                        <button
                          type="button"
                          className={styles.removeGenreBtn}
                          onClick={() => handleRemoveSocialLink(link)}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
                <div className={styles.addGenreRow}>
                  <input
                    type="url"
                    placeholder={t('profile.newSocialLinkPlaceholder')}
                    value={newSocialLink}
                    onChange={(e) => setNewSocialLink(e.target.value)}
                    onKeyDown={handleSocialLinkKeyDown}
                    className={styles.genreInput}
                  />
                  <button type="button" className={styles.addGenreBtn} onClick={handleAddSocialLink}>
                    +
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>{t('profile.favoriteGenres')}</label>
              <div className={styles.genreEditor}>
                <div className={styles.genreTagsEdit}>
                  {formData.favorite_genres.map((genre, index) => (
                    <span key={index} className={styles.genreTagEdit}>
                      {genre}
                      <button 
                        type="button" 
                        className={styles.removeGenreBtn}
                        onClick={() => handleRemoveGenre(genre)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className={styles.addGenreRow}>
                  <input
                    type="text"
                    placeholder={t('profile.newGenrePlaceholder')}
                    value={newGenre}
                    onChange={(e) => setNewGenre(e.target.value)}
                    onKeyDown={handleGenreKeyDown}
                    className={styles.genreInput}
                  />
                  <button type="button" className={styles.addGenreBtn} onClick={handleAddGenre}>
                    +
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.formActions}>
              <button type="button" className={styles.cancelBtn} onClick={handleCancel} disabled={isSaving}>
                {t('profile.cancel')}
              </button>
              <button type="submit" className={styles.saveBtn} disabled={isSaving}>
                {isSaving ? t('profile.saving') : t('profile.save')}
              </button>
            </div>
          </form>
        ) : (
          <div className={styles.infoList}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>{t('profile.username')}</span>
              <span className={styles.infoValue}>{formData.username}</span>
            </div>
            
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>{t('profile.email')}</span>
              <span className={styles.infoValue}>{formData.email || <span className={styles.notSet}>{t('profile.notProvided')}</span>}</span>
            </div>
            
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>{t('profile.bio')}</span>
              <span className={styles.infoValue}>{formData.bio || <span className={styles.notSet}>{t('profile.noBio')}</span>}</span>
            </div>
            
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>{t('profile.socialLinks')}</span>
              <span className={styles.infoValue}>
                {formData.social_links.length > 0 ? (
                  <div className={styles.socialLinksList}>
                    {formData.social_links.map((link, index) => {
                      const platform = getSocialPlatform(link);
                      const Icon = platform?.Icon;
                      return (
                        <a
                          key={index}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.socialLinkRow}
                        >
                          {Icon && (
                            <Icon className={styles.socialLinkIcon} style={platform.color ? { color: platform.color } : undefined} />
                          )}
                          <span className={styles.socialLinkPlatform}>
                            {platform?.label || t('profile.socialLinkOther')}
                          </span>
                        </a>
                      );
                    })}
                  </div>
                ) : (
                  <span className={styles.notSet}>{t('profile.noSocialLinks')}</span>
                )}
              </span>
            </div>
            
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>{t('profile.favoriteGenres')}</span>
              <span className={styles.infoValue}>
                {formData.favorite_genres.length > 0 ? (
                  <div className={styles.genreTags}>
                    {formData.favorite_genres.map((genre, index) => (
                      <span key={index} className={styles.genreTag}>{genre}</span>
                    ))}
                  </div>
                ) : (
                  <span className={styles.notSet}>{t('profile.noGenres')}</span>
                )}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Stats Section */}
      <div className={styles.statsSection}>
        <h2>📊 {t('profile.statistics')}</h2>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>📚</div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{stats.manga_count || 0}</div>
              <div className={styles.statLabel}>{t('profile.mangaSeries')}</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>📖</div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{stats.band_count || 0}</div>
              <div className={styles.statLabel}>{t('profile.totalVolumes')}</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>💫</div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{stats.wishlist_count || 0}</div>
              <div className={styles.statLabel}>{t('profile.onWishlist')}</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>✅</div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{stats.completed_manga_count || 0}</div>
              <div className={styles.statLabel}>{t('profile.completeSeries')}</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>👓</div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{stats.read_count || 0}</div>
              <div className={styles.statLabel}>{t('profile.volumesRead')}</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>📕</div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{stats.unread_count || 0}</div>
              <div className={styles.statLabel}>{t('profile.volumesUnread')}</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>❓</div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{stats.missing_bands_count || 0}</div>
              <div className={styles.statLabel}>{t('profile.volumesMissing')}</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>📗</div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{stats.read_manga_count || 0}</div>
              <div className={styles.statLabel}>{t('profile.seriesFullyRead')}</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>📈</div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{Math.round(stats.overall_collection_progress_percent || 0)}%</div>
              <div className={styles.statLabel}>{t('profile.collectionProgress')}</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>🔖</div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{Math.round(stats.overall_reading_progress_percent || 0)}%</div>
              <div className={styles.statLabel}>{t('profile.readingProgress')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
